/**
 * ConsultationWorkspace — clinical workspace for an active consultation.
 *
 * Fetches the consultation by ID, renders the card header (status, close
 * button, appointment details), and provides section editors for vitals,
 * SOAP notes, treatments, etc.
 *
 * Section visibility is managed at the workspace level via a per-section
 * `isActive` flag (derived from server data on mount, toggled by add/remove
 * actions). Inactive sections appear as compact add-cards in a row at the top.
 * Treatments are always visible.
 *
 * Each active section is self-contained — owns its own field state,
 * debounce-patches on edit, and deletes on "Remove". Sections signal unsaved
 * work via `setSectionBusy` so the workspace can block end/finalise actions
 * until all sections are idle.
 */

import { useQueryClient } from '@tanstack/react-query'
import {
  CalendarClock,
  Check,
  CheckCircle2,
  ChevronRight,
  CircleAlert,
  Loader2,
  Lock,
  MessageCircleMore,
  NotebookPen,
  Paperclip,
  Plus,
  X,
} from 'lucide-react'
import { useCallback, useRef, useState } from 'react'
import { toast } from 'sonner'

import {
  ALLOWED_CONTENT_TYPES,
  APPOINTMENT_TYPES_BY_ID,
  type ConsultationType,
  type ConsultationWithStock,
  deriveConsultationStatus,
  type FollowUpNote,
  formatInstantDate,
  formatName,
  type NoteType,
  type NoteWithStaff,
  resolveDateFormat,
  type SoapNote,
  type TextNote,
} from '@snootsvet/shared'

import { FileDownloadLink } from '@/frontend/components/file-download-link'
import { FormattedDateTime } from '@/frontend/components/formatted-datetime'
import { NumericInput } from '@/frontend/components/numeric-input'
import { Badge } from '@/frontend/components/ui/badge'
import { Button } from '@/frontend/components/ui/button'
import { ConfirmButton } from '@/frontend/components/ui/confirm-button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/frontend/components/ui/dialog'
import { Label } from '@/frontend/components/ui/label'
import { RichTextEditor } from '@/frontend/components/ui/rich-text-editor'
import { Skeleton } from '@/frontend/components/ui/skeleton'
import { ChangeAppointmentDialog } from '@/frontend/features/consultations/change-appointment-dialog'
import { useViewerRegion } from '@/frontend/features/region/region-context'
import { useGetConsultation } from '@/frontend/hooks/api/consultations/use-get-consultation'
import { usePatchConsultation } from '@/frontend/hooks/api/consultations/use-patch-consultation'
import { useCreateNote } from '@/frontend/hooks/api/notes/use-create-note'
import { useDeleteNote } from '@/frontend/hooks/api/notes/use-delete-note'
import { usePatchNote } from '@/frontend/hooks/api/notes/use-patch-note'
import { useCreateVitals } from '@/frontend/hooks/api/use-create-vitals'
import { useDeleteVitals } from '@/frontend/hooks/api/use-delete-vitals'
import { usePatchVitals } from '@/frontend/hooks/api/use-patch-vitals'
import { useUploadFile } from '@/frontend/hooks/use-upload-file'
import { cn } from '@/frontend/lib/utils'
import {
  DEFAULT_VITALS,
  getVitalsFields,
  type VitalsValues,
  vitalsFromServer,
  vitalsToApi,
} from '@/frontend/lib/vitals'

import { FollowUpTaskForm } from '../tasks/forms/follow-up-task.form'
import { useActiveConsultation } from './active-consultation-context'
import { ConsultationWorkspaceTreatments } from './consultation-workspace-treatments'

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface ConsultationWorkspaceProps {
  consultationId: string
}

/** Props shared by every section component. */
interface SectionProps {
  consultation: ConsultationWithStock
  /** Signal to the parent that a section has unsaved work (true) or is idle (false). */
  setSectionBusy: (key: string, busy: boolean) => void
  /** Remove the section from the workspace (called after cleanup). */
  onDeactivate: () => void
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

type SaveStatus = 'saving' | 'saved' | 'failed' | null

const DEBOUNCE_MS = 2000

const SOAP_FIELDS = [
  { key: 'subjective', label: 'Subjective', placeholder: 'Presenting complaint, history...' },
  { key: 'objective', label: 'Objective', placeholder: 'Examination findings...' },
  { key: 'assessment', label: 'Assessment', placeholder: 'Diagnosis or assessment...' },
  { key: 'plan', label: 'Plan', placeholder: 'Treatment plan...' },
] as const

// ---------------------------------------------------------------------------
// Section state types
// ---------------------------------------------------------------------------

type SectionKey = 'vitals' | 'soap' | 'clinical' | 'follow_up' | 'internal' | 'files'

interface SectionState {
  isActive: boolean
}

type SectionStates = Record<SectionKey, SectionState>

const SECTION_ADD_CARDS: { key: SectionKey; label: string }[] = [
  { key: 'vitals', label: 'Vitals' },
  { key: 'soap', label: 'SOAP Notes' },
  { key: 'clinical', label: 'Quick Note' },
  { key: 'follow_up', label: 'Follow-Up' },
  { key: 'internal', label: 'Internal Note' },
  { key: 'files', label: 'Files' },
]

/** Derive initial section states from server data. */
function deriveInitialSections(c: ConsultationWithStock): SectionStates {
  const on: SectionState = { isActive: true }
  const off: SectionState = { isActive: false }
  return {
    vitals: c.vitals ? on : off,
    soap: findNote(c.notes, 'soap') ? on : off,
    clinical: findNote(c.notes, 'clinical') ? on : off,
    follow_up: findNote(c.notes, 'follow_up') ? on : off,
    internal: findNote(c.notes, 'internal') ? on : off,
    files: (findNote<NoteWithStaff>(c.notes, 'general')?.files?.length ?? 0) > 0 ? on : off,
  }
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type SoapValues = { subjective: string; objective: string; assessment: string; plan: string }

const DEFAULT_SOAP: SoapValues = { subjective: '', objective: '', assessment: '', plan: '' }

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function findNote<T extends { type: NoteType }>(
  notes: ConsultationWithStock['notes'],
  type: NoteType
): T | undefined {
  return notes.find((n) => n.type === type) as T | undefined
}

// ---------------------------------------------------------------------------
// ConsultationSection — shared card chrome for workspace sections
// ---------------------------------------------------------------------------

function ConsultationSection({
  title,
  locked,
  removable = true,
  removeDisabled,
  saveStatus,
  onRemove,
  headerActions,
  children,
}: {
  title: string
  locked: boolean
  removable?: boolean
  removeDisabled?: boolean
  saveStatus?: SaveStatus
  onRemove: () => void
  headerActions?: React.ReactNode
  children: React.ReactNode
}) {
  const [isOpen, setIsOpen] = useState(true)

  return (
    <div className="rounded-lg border bg-card">
      <div className="flex items-center justify-between px-4 py-3 border-b">
        <button
          type="button"
          onClick={() => setIsOpen((o) => !o)}
          className="flex items-center gap-2 cursor-pointer"
        >
          <ChevronRight
            className={cn(
              'size-4 text-muted-foreground transition-transform',
              isOpen && 'rotate-90'
            )}
          />
          <span className="font-semibold">{title}</span>
          {saveStatus === 'failed' ? (
            <span className="flex items-center gap-1 text-sm font-medium text-destructive-fg">
              <CircleAlert className="size-3" /> Not saved
            </span>
          ) : (
            saveStatus && (
              <span className="flex items-center gap-1 text-sm font-medium text-success-fg">
                {saveStatus === 'saving' ? (
                  <>
                    <Loader2 className="size-3 animate-spin" /> Saving…
                  </>
                ) : (
                  <>
                    <Check className="size-3" /> Saved
                  </>
                )}
              </span>
            )
          )}
        </button>
        <div className="flex items-center gap-2">
          {headerActions}
          {!locked && removable && (
            <Button
              type="button"
              variant="ghost"
              disabled={removeDisabled}
              onClick={onRemove}
              className="h-7 px-2 text-sm text-destructive-fg"
            >
              Delete
            </Button>
          )}
        </div>
      </div>
      {isOpen && <div className="p-4">{children}</div>}
    </div>
  )
}

// ---------------------------------------------------------------------------
// VitalsSection
// ---------------------------------------------------------------------------

function VitalsSection({ consultation, setSectionBusy, onDeactivate }: SectionProps) {
  const locked = !!consultation.finalisedAt
  const serverVitals = consultation.vitals

  // useViewerRegion must run before useState below so `region` is in scope at init.
  const { region } = useViewerRegion()
  const fields = getVitalsFields(region)

  const [values, setValues] = useState<VitalsValues>(
    serverVitals ? vitalsFromServer(serverVitals, region) : DEFAULT_VITALS
  )
  const [saveStatus, setSaveStatus] = useState<SaveStatus>(null)

  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined)

  const { mutate: deleteVitals } = useDeleteVitals({
    patientId: consultation.patientId,
    consultationId: consultation.id,
  })
  const { mutateAsync: patchVitals } = usePatchVitals()

  function handleChange(next: VitalsValues) {
    if (locked) return
    setValues(next)
    setSaveStatus('saving')
    setSectionBusy('vitals', true)
    clearTimeout(timerRef.current)
    timerRef.current = setTimeout(async () => {
      const id = serverVitals?.id
      if (!id) return
      try {
        await patchVitals({ id, body: vitalsToApi(next, region) })
        setSaveStatus('saved')
        setSectionBusy('vitals', false)
      } catch {
        // Stays busy so the consultation can't be locked with this change unsaved.
        setSaveStatus('failed')
        toast.error('Failed to save vitals')
      }
    }, DEBOUNCE_MS)
  }

  function handleRemove() {
    clearTimeout(timerRef.current)
    setSectionBusy('vitals', false)
    setValues(DEFAULT_VITALS)
    if (serverVitals?.id) {
      deleteVitals(serverVitals.id, {
        onError: (err) =>
          toast.error(err instanceof Error ? err.message : 'Failed to delete vitals'),
      })
    }
    onDeactivate()
  }

  return (
    <ConsultationSection
      title="Vitals"
      locked={locked}
      saveStatus={saveStatus}
      onRemove={handleRemove}
    >
      <div className="grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-3">
        {fields.map(({ key, label, unit }) => (
          <div key={key} className="flex flex-col gap-1">
            <Label className="text-sm text-muted-foreground">
              {label}
              <span className="ml-1 font-normal text-muted-foreground/60">{unit}</span>
            </Label>
            <NumericInput
              value={values[key]}
              onChange={(val) => handleChange({ ...values, [key]: val })}
              disabled={locked}
              placeholder="—"
            />
          </div>
        ))}
      </div>
    </ConsultationSection>
  )
}

// ---------------------------------------------------------------------------
// SoapSection
// ---------------------------------------------------------------------------

function SoapSection({ consultation, setSectionBusy, onDeactivate }: SectionProps) {
  const locked = !!consultation.finalisedAt
  const server = findNote<SoapNote>(consultation.notes, 'soap')

  const valuesRef = useRef<SoapValues>(
    server
      ? {
          subjective: server.content.subjective ?? '',
          objective: server.content.objective ?? '',
          assessment: server.content.assessment ?? '',
          plan: server.content.plan ?? '',
        }
      : DEFAULT_SOAP
  )
  const [saveStatus, setSaveStatus] = useState<SaveStatus>(null)

  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined)

  const { mutate: deleteNote } = useDeleteNote({
    patientId: consultation.patientId,
    consultationId: consultation.id,
  })
  const { mutateAsync: patchNote } = usePatchNote()

  function handleChange(key: keyof SoapValues, html: string) {
    if (locked) return
    valuesRef.current = { ...valuesRef.current, [key]: html }
    setSaveStatus('saving')
    setSectionBusy('soap', true)
    clearTimeout(timerRef.current)
    timerRef.current = setTimeout(async () => {
      const id = server?.id
      if (!id) return
      try {
        await patchNote({ id, body: { content: valuesRef.current } })
        setSaveStatus('saved')
        setSectionBusy('soap', false)
      } catch {
        // Stays busy so the consultation can't be locked with this change unsaved.
        setSaveStatus('failed')
        toast.error('Failed to save notes')
      }
    }, DEBOUNCE_MS)
  }

  function handleRemove() {
    clearTimeout(timerRef.current)
    setSectionBusy('soap', false)
    valuesRef.current = DEFAULT_SOAP
    if (server?.id) {
      deleteNote(server.id, {
        onError: (err) => toast.error(err instanceof Error ? err.message : 'Failed to delete note'),
      })
    }
    onDeactivate()
  }

  return (
    <ConsultationSection
      title="SOAP Notes"
      locked={locked}
      saveStatus={saveStatus}
      onRemove={handleRemove}
    >
      <div className="flex min-w-0 flex-col gap-4">
        {SOAP_FIELDS.map(({ key, label, placeholder }) => (
          <div key={key} className="flex min-w-0 flex-col gap-1.5">
            <Label className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
              {label}
            </Label>
            <RichTextEditor
              value={valuesRef.current[key] || undefined}
              onChange={(html) => handleChange(key, html)}
              disabled={locked}
              placeholder={placeholder}
            />
          </div>
        ))}
      </div>
    </ConsultationSection>
  )
}

// ---------------------------------------------------------------------------
// TextNoteSection — reusable for quick note & internal note
// ---------------------------------------------------------------------------

interface TextNoteSectionProps extends SectionProps {
  noteType: NoteType
  title: string
  placeholder: string
  sublabel?: string
}

function TextNoteSection({
  consultation,
  setSectionBusy,
  onDeactivate,
  noteType,
  title,
  placeholder,
  sublabel,
}: TextNoteSectionProps) {
  const locked = !!consultation.finalisedAt
  const server = findNote<TextNote>(consultation.notes, noteType)

  const [saveStatus, setSaveStatus] = useState<SaveStatus>(null)

  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined)

  const { mutate: deleteNote } = useDeleteNote({
    patientId: consultation.patientId,
    consultationId: consultation.id,
  })
  const { mutateAsync: patchNote } = usePatchNote()

  function handleChange(html: string) {
    if (locked) return
    setSaveStatus('saving')
    setSectionBusy(noteType, true)
    clearTimeout(timerRef.current)
    timerRef.current = setTimeout(async () => {
      const id = server?.id
      if (!id) return
      try {
        await patchNote({ id, body: { content: { text: html } } })
        setSaveStatus('saved')
        setSectionBusy(noteType, false)
      } catch {
        // Stays busy so the consultation can't be locked with this change unsaved.
        setSaveStatus('failed')
        toast.error('Failed to save note')
      }
    }, DEBOUNCE_MS)
  }

  function handleRemove() {
    clearTimeout(timerRef.current)
    setSectionBusy(noteType, false)
    if (server?.id) {
      deleteNote(server.id, {
        onError: (err) => toast.error(err instanceof Error ? err.message : 'Failed to delete note'),
      })
    }
    onDeactivate()
  }

  return (
    <ConsultationSection
      title={title}
      locked={locked}
      saveStatus={saveStatus}
      onRemove={handleRemove}
    >
      <div className="flex min-w-0 flex-col gap-3">
        <RichTextEditor
          value={server?.content.text || undefined}
          onChange={handleChange}
          disabled={locked}
          placeholder={placeholder}
        />
        {sublabel && <p className="text-sm text-muted-foreground">{sublabel}</p>}
      </div>
    </ConsultationSection>
  )
}

// ---------------------------------------------------------------------------
// FollowUpSection
// ---------------------------------------------------------------------------

function FollowUpSection({ consultation, setSectionBusy, onDeactivate }: SectionProps) {
  // Deliberately the helper, not <FormattedDate>: this date goes into a task title
  // that gets *persisted*. The components carry the viewer's date order and clock
  // convention and annotate the viewer's own reading, which is right on screen and
  // wrong in stored data — a US and a UK vet creating the same follow-up would
  // write different titles. So this takes the owning member's region explicitly,
  // as all generated output should.
  const region = consultation.patient.owner.region
  const locked = !!consultation.finalisedAt
  const server = findNote<FollowUpNote>(consultation.notes, 'follow_up')

  const [taskId, setTaskId] = useState(server?.content.taskId)
  // Auto-open dialog when section is activated without existing data
  const [dialogOpen, setDialogOpen] = useState(!server)
  const [saveStatus, setSaveStatus] = useState<SaveStatus>(null)

  const [initialContent, setInitialContent] = useState(server?.content.text)
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined)

  const { mutate: createNote } = useCreateNote()
  const { mutate: deleteNote } = useDeleteNote({
    patientId: consultation.patientId,
    consultationId: consultation.id,
  })
  const { mutateAsync: patchNote } = usePatchNote()

  function handleChange(html: string, currentTaskId?: string) {
    if (locked) return
    setSaveStatus('saving')
    setSectionBusy('follow_up', true)
    clearTimeout(timerRef.current)
    timerRef.current = setTimeout(async () => {
      const id = server?.id
      if (!id) return
      try {
        await patchNote({ id, body: { content: { text: html, taskId: currentTaskId } } })
        setSaveStatus('saved')
        setSectionBusy('follow_up', false)
      } catch {
        // Stays busy so the consultation can't be locked with this change unsaved.
        setSaveStatus('failed')
        toast.error('Failed to save follow-up')
      }
    }, DEBOUNCE_MS)
  }

  function handleTaskCreated(task: { id: string; title: string }) {
    setInitialContent(task.title)
    setTaskId(task.id)
    setDialogOpen(false)
    createNote(
      {
        patientId: consultation.patientId,
        consultationId: consultation.id,
        type: 'follow_up',
        content: { text: task.title, taskId: task.id },
      },
      {
        onError: (err) => {
          toast.error(err instanceof Error ? err.message : 'Failed to create follow-up note')
          setInitialContent(undefined)
          setTaskId(undefined)
          onDeactivate()
        },
      }
    )
  }

  function handleDialogOpenChange(open: boolean) {
    setDialogOpen(open)
    // User cancelled the dialog before creating anything — deactivate
    if (!open && !server && !taskId) {
      onDeactivate()
    }
  }

  function handleRemove() {
    clearTimeout(timerRef.current)
    setSectionBusy('follow_up', false)
    setInitialContent(undefined)
    setTaskId(undefined)
    if (server?.id) {
      deleteNote(server.id, {
        onError: (err) => toast.error(err instanceof Error ? err.message : 'Failed to delete note'),
      })
    }
    onDeactivate()
  }

  // Dialog-only state: section activated but no data yet
  if (!server && !initialContent) {
    return (
      <Dialog open={dialogOpen} onOpenChange={handleDialogOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Follow-Up Task</DialogTitle>
          </DialogHeader>
          <FollowUpTaskForm
            defaultValues={{
              patientId: consultation.patientId,
              title: (() => {
                const typeName = consultation.appointment
                  ? (APPOINTMENT_TYPES_BY_ID[consultation.appointment.appointmentTypeId]?.name ??
                    null)
                  : null
                const vetName = consultation.staff ? formatName(consultation.staff) : ''
                const date = formatInstantDate(
                  consultation.appointment?.startTime ?? consultation.createdAt,
                  resolveDateFormat(consultation.clinicId, region)
                )
                return [typeName, vetName ? `with ${vetName}` : null, `on ${date}`]
                  .filter(Boolean)
                  .join(' ')
              })(),
            }}
            onSuccess={handleTaskCreated}
            onCancel={() => handleDialogOpenChange(false)}
          />
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <ConsultationSection
      title="Follow-Up"
      locked={locked}
      removable={!taskId}
      saveStatus={saveStatus}
      onRemove={handleRemove}
    >
      <div className="flex min-w-0 flex-col gap-3">
        <RichTextEditor
          value={initialContent}
          onChange={(html) => handleChange(html, taskId)}
          disabled={locked}
          placeholder="Follow-up notes..."
        />
        {taskId && (
          <p className="flex items-center gap-1.5 text-sm text-success-fg font-medium">
            <CheckCircle2 className="size-3.5" />
            Follow-up task created
          </p>
        )}
      </div>
    </ConsultationSection>
  )
}

// ---------------------------------------------------------------------------
// FilesSection
// ---------------------------------------------------------------------------

function FilesSection({ consultation, setSectionBusy, onDeactivate }: SectionProps) {
  const locked = !!consultation.finalisedAt
  const attachmentNote = findNote<NoteWithStaff>(consultation.notes, 'general') ?? null
  const attachedFiles = attachmentNote?.files ?? []

  const [isUploading, setIsUploading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const uploadingRef = useRef(false)
  const localNoteIdRef = useRef<string | null>(null)

  const { mutateAsync: uploadFile } = useUploadFile()
  const { mutateAsync: createNote } = useCreateNote()
  const { mutateAsync: patchNote } = usePatchNote()

  async function handleFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? [])
    if (files.length === 0 || uploadingRef.current) return
    e.target.value = ''

    uploadingRef.current = true
    setIsUploading(true)
    setSectionBusy('files', true)
    try {
      const fileKeys = await Promise.all(files.map((f) => uploadFile(f)))
      const effectiveNoteId = attachmentNote?.id ?? localNoteIdRef.current
      if (!effectiveNoteId) {
        const created = await createNote({
          patientId: consultation.patientId,
          consultationId: consultation.id,
          type: 'general',
          content: { text: '' },
          fileKeys,
        })
        localNoteIdRef.current = created.id
      } else {
        await patchNote({ id: effectiveNoteId, body: { addFileKeys: fileKeys } })
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to attach file')
    } finally {
      uploadingRef.current = false
      setIsUploading(false)
      setSectionBusy('files', false)
    }
  }

  async function handleRemoveFile(fileId: string) {
    const effectiveNoteId = attachmentNote?.id ?? localNoteIdRef.current
    if (!effectiveNoteId) return
    setSectionBusy('files', true)
    try {
      await patchNote({ id: effectiveNoteId, body: { removeFileIds: [fileId] } })
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to remove file')
    } finally {
      setSectionBusy('files', false)
    }
  }

  const fileUploadButton = !locked ? (
    <>
      <Button
        type="button"
        variant="ghost"
        disabled={isUploading}
        onClick={() => fileInputRef.current?.click()}
      >
        {isUploading ? (
          <Loader2 className="size-3.5 mr-1.5 animate-spin" />
        ) : (
          <Paperclip className="size-3.5 mr-1.5" />
        )}
        {isUploading ? 'Uploading...' : 'Add file'}
      </Button>
      <input
        ref={fileInputRef}
        type="file"
        multiple
        className="hidden"
        accept={ALLOWED_CONTENT_TYPES.join(',')}
        onChange={handleFileSelect}
      />
    </>
  ) : null

  return (
    <ConsultationSection
      title="Files"
      locked={locked}
      removable
      removeDisabled={attachedFiles.length > 0 || isUploading}
      onRemove={onDeactivate}
      headerActions={fileUploadButton}
    >
      {attachedFiles.length === 0 ? (
        <p className="text-muted-foreground">No files attached.</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {attachedFiles.map((f) => (
            <li key={f.id} className="flex items-center justify-between gap-2">
              <FileDownloadLink fileId={f.id} fileName={f.fileName} />
              {!locked && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  title="Remove file"
                  onClick={() => handleRemoveFile(f.id)}
                >
                  <X className="size-3.5" />
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
    </ConsultationSection>
  )
}

// ---------------------------------------------------------------------------
// ConsultationWorkspace
// ---------------------------------------------------------------------------

export function ConsultationWorkspace({ consultationId }: ConsultationWorkspaceProps) {
  const { close } = useActiveConsultation()
  const { data: consultation, isLoading } = useGetConsultation(consultationId)

  if (isLoading || !consultation) {
    return (
      <div className="overflow-hidden rounded-xl bg-card ring-2 ring-primary/30 p-6">
        <div className="space-y-4">
          <Skeleton className="h-5 w-48" />
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
      </div>
    )
  }

  return (
    <ConsultationWorkspaceInner key={consultation.id} consultation={consultation} onClose={close} />
  )
}

function ConsultationWorkspaceInner({
  consultation,
  onClose,
}: {
  consultation: ConsultationWithStock
  onClose: () => void
}) {
  const consultationType: ConsultationType = consultation.clinicId ? 'clinic' : 'remote'
  const locked = !!consultation.finalisedAt
  const status = deriveConsultationStatus(consultation)

  // -- Section visibility ---------------------------------------------------

  const [sections, setSections] = useState<SectionStates>(() => deriveInitialSections(consultation))

  const inactiveKeys = SECTION_ADD_CARDS.filter(({ key }) => !sections[key].isActive)

  const queryClient = useQueryClient()
  const { mutateAsync: createVitals } = useCreateVitals()
  const { mutateAsync: createNote } = useCreateNote()
  const [pendingSection, setPendingSection] = useState<SectionKey | null>(null)

  const activateSection = async (key: SectionKey) => {
    // follow_up and files handle their own creation flow internally
    if (key === 'follow_up' || key === 'files') {
      setSections((prev) => ({ ...prev, [key]: { isActive: true } }))
      return
    }

    const base = { patientId: consultation.patientId, consultationId: consultation.id }
    setPendingSection(key)

    try {
      switch (key) {
        case 'vitals':
          await createVitals({ ...base, source: 'clinical' as const })
          break
        case 'soap':
          await createNote({
            ...base,
            type: 'soap',
            content: { subjective: '', objective: '', assessment: '', plan: '' },
          })
          break
        case 'clinical':
        case 'internal':
          await createNote({ ...base, type: key, content: { text: '' } })
          break
      }
      setSections((prev) => ({ ...prev, [key]: { isActive: true } }))
    } catch (err) {
      if (err instanceof Error && err.name === 'ConflictError') {
        await queryClient.invalidateQueries({ queryKey: ['consultation', consultation.id] })
        setSections((prev) => ({ ...prev, [key]: { isActive: true } }))
      } else {
        toast.error(err instanceof Error ? err.message : 'Failed to add section')
      }
    } finally {
      setPendingSection(null)
    }
  }

  const deactivateSection = (key: SectionKey) => {
    setSections((prev) => ({ ...prev, [key]: { isActive: false } }))
  }

  // -- Busy / save state ----------------------------------------------------

  const [busySections, setBusySections] = useState(new Set<string>())
  const isBusy = busySections.size > 0

  const setSectionBusy = useCallback((key: string, busy: boolean) => {
    setBusySections((prev) => {
      if (busy === prev.has(key)) return prev
      const next = new Set(prev)
      busy ? next.add(key) : next.delete(key)
      return next
    })
  }, [])

  // Warn before closing if any section (including treatments) is busy
  const closeWarning = isBusy ? "Some changes haven't saved yet." : null

  /** Whether consultation-level actions (end, finalise, close) should be blocked. */
  const actionsBlocked = isBusy
  // -- Consultation actions -------------------------------------------------

  const { mutateAsync: patchConsultation } = usePatchConsultation()

  const [showChangeAppointment, setShowChangeAppointment] = useState(false)

  async function handleFinalise() {
    try {
      await patchConsultation({ id: consultation.id, body: { action: 'finalise' } })
      toast.success('Consultation locked')
      onClose()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to lock consultation')
    }
  }

  const appointmentTypeName = consultation.appointment
    ? (APPOINTMENT_TYPES_BY_ID[consultation.appointment.appointmentTypeId]?.name ?? null)
    : null

  return (
    <div className="overflow-hidden rounded-xl bg-card ring-2 ring-primary/30">
      {/* Header */}
      <div className="flex items-center justify-between gap-4 px-4 py-3 border-b">
        <div className="flex items-baseline gap-2 min-w-0">
          <span className="font-bold">{appointmentTypeName ?? 'Consultation'}</span>
          <span className="text-sm text-muted-foreground">
            <FormattedDateTime
              value={consultation.appointment?.startTime ?? consultation.createdAt}
              clinicId={consultation.clinicId}
            />
            {' · '}
            {consultation.staff?.firstName} {consultation.staff?.lastName}
            {consultation.clinicId
              ? consultation.clinic && ` · ${consultation.clinic.name}`
              : ' · Remote'}
          </span>
          {status === 'in_progress' && (
            <Badge variant="secondary" size="sm">
              In Progress
            </Badge>
          )}
          {status === 'finalised' && (
            <Badge
              variant="outline"
              size="sm"
              className="border-success-border bg-success text-success-fg"
            >
              Locked 🔒
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          {!locked && (
            <Button
              variant="ghost"
              onClick={() => setShowChangeAppointment(true)}
              className="text-muted-foreground"
            >
              <CalendarClock className="size-3.5" />
              {consultation.appointment ? 'Change appointment' : 'Link appointment'}
            </Button>
          )}
          {locked ? (
            <span className="flex items-center gap-1.5 text-muted-foreground font-medium">
              <Lock className="size-3.5" />
              Locked
            </span>
          ) : (
            <ConfirmButton
              heading="Lock consultation?"
              description="This will seal the consultation, preventing any further changes."
              confirmLabel="Lock Consultation"
              onConfirm={handleFinalise}
              confirmPending={actionsBlocked}
              disabled={actionsBlocked}
            >
              Lock Consultation
            </ConfirmButton>
          )}
          <ConfirmButton
            variant="ghost"
            size="icon-sm"
            heading="Close consultation?"
            description={closeWarning ?? ''}
            confirmLabel="Close anyway"
            bypass={!closeWarning}
            onConfirm={onClose}
          >
            <X className="size-4" />
          </ConfirmButton>
        </div>
      </div>

      <ChangeAppointmentDialog
        consultationId={consultation.id}
        patientId={consultation.patientId}
        currentAppointmentId={consultation.appointment?.id ?? null}
        open={showChangeAppointment}
        onOpenChange={setShowChangeAppointment}
      />

      {/* Appointment details */}
      {(consultation.appointment?.visitReason || consultation.appointment?.userNotes) && (
        <div className="border-b px-4 py-3 space-y-1">
          {consultation.appointment.visitReason && (
            <div className="flex items-start gap-1.5 text-sm text-muted-foreground">
              <NotebookPen className="mt-0.5 size-3 shrink-0" />
              <p>{consultation.appointment.visitReason}</p>
            </div>
          )}
          {consultation.appointment.userNotes && (
            <div className="flex items-start gap-1.5 text-sm text-muted-foreground">
              <MessageCircleMore className="mt-0.5 size-3 shrink-0" />
              <p>{consultation.appointment.userNotes}</p>
            </div>
          )}
        </div>
      )}
      {consultation.reason && (
        <div className="border-b px-4 py-3">
          <div className="flex items-start gap-1.5 text-sm text-muted-foreground">
            <NotebookPen className="mt-0.5 size-3 shrink-0" />
            <p>{consultation.reason}</p>
          </div>
        </div>
      )}

      {/* Sections */}
      <div className="p-4">
        <div className="flex flex-col gap-3">
          {/* Add-section cards for inactive sections */}
          {!locked && inactiveKeys.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {inactiveKeys.map(({ key, label }) => {
                const isLoading = pendingSection === key
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => activateSection(key)}
                    disabled={pendingSection !== null}
                    className="flex items-center gap-1.5 rounded-lg border border-dashed border-muted-foreground/30 px-3 py-2 text-muted-foreground hover:text-foreground hover:border-foreground/30 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isLoading ? (
                      <Loader2 className="size-3.5 animate-spin" />
                    ) : (
                      <Plus className="size-3.5" />
                    )}
                    {label}
                  </button>
                )
              })}
            </div>
          )}

          {/* Active sections in fixed order */}
          {sections.vitals.isActive && (
            <VitalsSection
              consultation={consultation}
              setSectionBusy={setSectionBusy}
              onDeactivate={() => deactivateSection('vitals')}
            />
          )}
          {sections.soap.isActive && (
            <SoapSection
              consultation={consultation}
              setSectionBusy={setSectionBusy}
              onDeactivate={() => deactivateSection('soap')}
            />
          )}
          {sections.clinical.isActive && (
            <TextNoteSection
              consultation={consultation}
              setSectionBusy={setSectionBusy}
              onDeactivate={() => deactivateSection('clinical')}
              noteType="clinical"
              title="Quick Note"
              placeholder="Clinical observations..."
            />
          )}
          {/* Treatments — always visible */}
          <ConsultationWorkspaceTreatments
            consultation={consultation}
            consultationType={consultationType}
            setSectionBusy={setSectionBusy}
            readOnly={locked}
          />
          {sections.follow_up.isActive && (
            <FollowUpSection
              consultation={consultation}
              setSectionBusy={setSectionBusy}
              onDeactivate={() => deactivateSection('follow_up')}
            />
          )}
          {sections.internal.isActive && (
            <TextNoteSection
              consultation={consultation}
              setSectionBusy={setSectionBusy}
              onDeactivate={() => deactivateSection('internal')}
              noteType="internal"
              title="Internal Note"
              placeholder="Private notes — not shared with the owner..."
              sublabel="Only visible to practice staff."
            />
          )}
          {sections.files.isActive && (
            <FilesSection
              consultation={consultation}
              setSectionBusy={setSectionBusy}
              onDeactivate={() => deactivateSection('files')}
            />
          )}
        </div>
      </div>
    </div>
  )
}
